import type { Question, QuestionType } from '@voice2me/shared';
import { newId } from './api.js';

const typeLabels: Record<QuestionType, string> = {
  multiple_choice: 'Multiple choice',
  boolean: 'Yes / No',
  rating: 'Rating scale',
  open_text: 'Free text',
};

/** Builds a fresh question of the requested type, keeping the id so answers stay interpretable. */
export function questionOfType(type: QuestionType, existing?: Question): Question {
  const id = existing?.id ?? newId('q');
  const base = {
    id,
    prompt: existing?.prompt ?? '',
    required: existing?.required ?? true,
    ...(existing?.helpText ? { helpText: existing.helpText } : {}),
  };

  switch (type) {
    case 'multiple_choice':
      return {
        ...base,
        type,
        allowMultiple: false,
        options: [
          { id: newId('opt'), label: '' },
          { id: newId('opt'), label: '' },
        ],
      };
    case 'boolean':
      return { ...base, type };
    case 'rating':
      return { ...base, type, min: 1, max: 5 };
    case 'open_text':
      return { ...base, type, maxLength: 2000 };
  }
}

type Props = {
  question: Question;
  index: number;
  total: number;
  onChange: (question: Question) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
};

export function QuestionEditor({ question, index, total, onChange, onRemove, onMove }: Props) {
  return (
    <div className="question-editor">
      <div className="question-editor-header">
        <strong>Question {index + 1}</strong>
        <div className="question-editor-actions">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0}>
            Move up
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}>
            Move down
          </button>
          <button type="button" onClick={onRemove}>
            Remove
          </button>
        </div>
      </div>

      <label>
        Question text
        <input
          type="text"
          value={question.prompt}
          onChange={(event) => onChange({ ...question, prompt: event.target.value })}
        />
      </label>

      <label>
        Type
        <select
          value={question.type}
          onChange={(event) =>
            onChange(questionOfType(event.target.value as QuestionType, question))
          }
        >
          {Object.entries(typeLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label className="inline">
        <input
          type="checkbox"
          checked={question.required}
          onChange={(event) => onChange({ ...question, required: event.target.checked })}
        />
        Required
      </label>

      {question.type === 'multiple_choice' && (
        <>
          <label className="inline">
            <input
              type="checkbox"
              checked={question.allowMultiple}
              onChange={(event) => onChange({ ...question, allowMultiple: event.target.checked })}
            />
            Allow more than one answer
          </label>

          <fieldset>
            <legend>Options</legend>
            {question.options.map((option, optionIndex) => (
              <div key={option.id} className="option-row">
                <input
                  type="text"
                  value={option.label}
                  placeholder={`Option ${optionIndex + 1}`}
                  onChange={(event) =>
                    onChange({
                      ...question,
                      options: question.options.map((candidate) =>
                        candidate.id === option.id
                          ? { ...candidate, label: event.target.value }
                          : candidate,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  disabled={question.options.length <= 2}
                  onClick={() =>
                    onChange({
                      ...question,
                      options: question.options.filter((candidate) => candidate.id !== option.id),
                    })
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...question,
                  options: [...question.options, { id: newId('opt'), label: '' }],
                })
              }
            >
              Add option
            </button>
          </fieldset>
        </>
      )}

      {question.type === 'rating' && (
        <div className="row">
          <label>
            From
            <input
              type="number"
              min={0}
              max={10}
              value={question.min}
              onChange={(event) => onChange({ ...question, min: Number(event.target.value) })}
            />
          </label>
          <label>
            To
            <input
              type="number"
              min={1}
              max={10}
              value={question.max}
              onChange={(event) => onChange({ ...question, max: Number(event.target.value) })}
            />
          </label>
          <label>
            Low label
            <input
              type="text"
              value={question.minLabel ?? ''}
              onChange={(event) => onChange({ ...question, minLabel: event.target.value })}
            />
          </label>
          <label>
            High label
            <input
              type="text"
              value={question.maxLabel ?? ''}
              onChange={(event) => onChange({ ...question, maxLabel: event.target.value })}
            />
          </label>
        </div>
      )}

      {question.type === 'open_text' && (
        <label>
          Maximum characters
          <input
            type="number"
            min={1}
            max={10000}
            value={question.maxLength}
            onChange={(event) => onChange({ ...question, maxLength: Number(event.target.value) })}
          />
        </label>
      )}
    </div>
  );
}
